import { useMemo } from 'react';

import { Locale, addMonths, endOfDay, format, parse } from 'date-fns';

import {
  DAYS_OF_THE_WEEK_IN_ENGLISH,
  DEFAULT_TIME,
  INTERVAL_UNITS,
} from '../helpers/consts';
import { FormattedIntervalUnit, IntervalUnit, RunOn } from '../types';

const getRunAtTime = ({
  runAtTimeProp = DEFAULT_TIME,
  localeObject,
}: {
  runAtTimeProp?: string;
  localeObject?: Locale;
}) => {
  const parsed = parse(runAtTimeProp, 'h:mm a', new Date());
  return format(parsed, 'p', { locale: localeObject });
};

const calculateEndDate = (todayDate: Date, maxEndDate?: Date) => {
  const sixMonthsFromNow = addMonths(endOfDay(todayDate), 6);

  if (maxEndDate && maxEndDate < sixMonthsFromNow) {
    return maxEndDate;
  } else {
    return sixMonthsFromNow;
  }
};

const useScheduleInitialState = ({
  localeObject,
  todayDate,
  todayWeekDay,
  todayDateNumber,
  formattedIntervalUnits,
  runAtTimes,
  runOn: runOnProp,
  intervalUnit: intervalUnitProp,
  runAtTime: runAtTimeProp,
  startDate: startDateProp,
  endDate: endDateProp,
  unitCycle: unitCycleProp,
  maxEndDate,
}: {
  localeObject: Locale;
  todayDate: Date;
  todayWeekDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  todayDateNumber: number;
  formattedIntervalUnits: FormattedIntervalUnit[];
  runAtTimes: {
    label: string;
    value: string;
  }[];
  runOn?: RunOn[];
  intervalUnit?: IntervalUnit;
  runAtTime?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  unitCycle?: string;
  maxEndDate?: Date;
}) => {
  const initialIntervalUnit =
    (intervalUnitProp &&
      formattedIntervalUnits.find((u) => u.value === intervalUnitProp)) ||
    formattedIntervalUnits.find((u) => u.value === INTERVAL_UNITS.WEEK) ||
    formattedIntervalUnits[0];

  const intervalUnitRunOnMap: Record<IntervalUnit, RunOn[]> = {
    [INTERVAL_UNITS.DAY]: [null],
    [INTERVAL_UNITS.WEEK]: [DAYS_OF_THE_WEEK_IN_ENGLISH[todayWeekDay]],
    [INTERVAL_UNITS.MONTH]: [todayDateNumber],
  };

  const fallbackRunOn = initialIntervalUnit.value
    ? intervalUnitRunOnMap[initialIntervalUnit.value]
    : [];

  const runAtTime = useMemo(
    () => getRunAtTime({ runAtTimeProp, localeObject }),
    [localeObject, runAtTimeProp],
  );

  const initialRunAtTime = useMemo(
    () => runAtTimes.find((t) => t.value === runAtTime),
    [runAtTime, runAtTimes],
  );

  const initialStartDate = startDateProp || todayDate;
  const initialEndDate =
    endDateProp || calculateEndDate(initialStartDate, maxEndDate);

  return {
    initialUnitCycle: unitCycleProp ?? '1',
    initialRunAtTime,
    initialRunOn: runOnProp || fallbackRunOn,
    initialIntervalUnit,
    initialStartDate,
    initialEndDate,
  };
};

export default useScheduleInitialState;
